import { CONNECTION_PROVIDER_LABELS, type ConnectionProvider } from "@healthos/shared";
import type { ComponentType, SVGProps } from "react";
import {
  SiApple,
  SiFitbit,
  SiGarmin,
  SiGooglecalendar,
  SiGooglefit,
  SiPeloton,
  SiStrava,
} from "react-icons/si";
import { cn } from "../lib/cn";

/**
 * ConnectionLogo, brand marks for data-source providers on the Connections
 * page.
 *
 * We prefer real, recognisable brand SVGs from Simple Icons
 * (`react-icons/si`, CC0 licensed, ~3000 brands). For providers that aren't
 * in Simple Icons (Dexcom, Epic MyChart, Cerner, MyFitnessPal, WHOOP, Polar,
 * Ultrahuman, Flow) we ship a
 * hand-tuned inline SVG that reads as the category.
 *
 * Each chip uses a branded tint so the grid reads as a set of identities,
 * not a generic icon row.
 */

export type { ConnectionProvider } from "@healthos/shared";

interface ConnectionLogoProps {
  provider: ConnectionProvider;
  size?: number;
  className?: string;
  /** "glyph" renders the bare brand mark without the tinted tile. */
  variant?: "tile" | "glyph";
}

const TINT: Record<ConnectionProvider, { bg: string; fg: string }> = {
  terra: { bg: "bg-teal-50", fg: "text-teal-700" },
  "apple-health": { bg: "bg-rose-50", fg: "text-rose-600" },
  "health-connect": { bg: "bg-green-50", fg: "text-green-700" },
  google: { bg: "bg-blue-50", fg: "text-blue-700" },
  "google-health": { bg: "bg-blue-50", fg: "text-blue-600" },
  fitbit: { bg: "bg-teal-50", fg: "text-teal-700" },
  oura: { bg: "bg-gray-50", fg: "text-gray-900" },
  garmin: { bg: "bg-blue-50", fg: "text-blue-700" },
  whoop: { bg: "bg-stone-50", fg: "text-stone-900" },
  polar: { bg: "bg-sky-50", fg: "text-sky-700" },
  peloton: { bg: "bg-red-50", fg: "text-red-600" },
  ultrahuman: { bg: "bg-violet-50", fg: "text-violet-700" },
  strava: { bg: "bg-orange-50", fg: "text-orange-600" },
  myfitnesspal: { bg: "bg-blue-50", fg: "text-blue-700" },
  flow: { bg: "bg-pink-50", fg: "text-pink-700" },
  withings: { bg: "bg-cyan-50", fg: "text-cyan-700" },
  dexcom: { bg: "bg-blue-50", fg: "text-blue-700" },
  "calendar-google": { bg: "bg-blue-50", fg: "text-blue-600" },
  "calendar-microsoft": { bg: "bg-blue-50", fg: "text-blue-600" },
  "ehr-epic": { bg: "bg-rose-50", fg: "text-rose-700" },
  "ehr-cerner": { bg: "bg-purple-50", fg: "text-purple-700" },
};

/* --------------------------- Fallback glyphs ----------------------------
 * For brands Simple Icons doesn't cover we ship inline SVGs. Each marks
 * the category (CGM / EHR / nutrition / Outlook) in a way that's
 * recognisable even when the official wordmark isn't available.
 */

type SvgProps = SVGProps<SVGSVGElement>;

function MyFitnessPalGlyph(props: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" role="img" aria-hidden="true" {...props}>
      <title>MyFitnessPal</title>
      <path
        d="M4.5 16.5c2.8-5.8 5.3-8.7 7.5-8.7s4.7 2.9 7.5 8.7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M8.2 16.2c1.3-2.6 2.6-3.9 3.8-3.9s2.5 1.3 3.8 3.9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function DexcomGlyph(props: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" role="img" aria-hidden="true" {...props}>
      <title>Dexcom CGM</title>
      <path
        d="M12 3c-3.5 4.3-5.5 7.6-5.5 10.5a5.5 5.5 0 0 0 11 0C17.5 10.6 15.5 7.3 12 3Z"
        fill="currentColor"
        opacity="0.2"
      />
      <path
        d="M12 3c-3.5 4.3-5.5 7.6-5.5 10.5a5.5 5.5 0 0 0 11 0C17.5 10.6 15.5 7.3 12 3Z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path
        d="M8.5 14h1.3l1 2 1.4-3 1.3 2H16"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EpicMyChartGlyph(props: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" role="img" aria-hidden="true" {...props}>
      <title>Epic MyChart</title>
      <rect x="3.5" y="3.5" width="17" height="17" rx="3" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M7 15v-3M11 15V9M15 15v-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function OracleCernerGlyph(props: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" role="img" aria-hidden="true" {...props}>
      <title>Oracle Cerner</title>
      <path
        d="M7 8.5A5 5 0 0 1 15 5.5M17 15.5A5 5 0 0 1 9 18.5"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TerraGlyph(props: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" role="img" aria-hidden="true" {...props}>
      <title>Wearables</title>
      <path
        d="M12 4C7.5 4 4 7.5 4 12s3.5 8 8 8 8-3.5 8-8-3.5-8-8-8Z"
        stroke="currentColor"
        strokeWidth="1.6"
        fill="none"
      />
      <path
        d="M8 12l2 2 4-4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WhoopGlyph(props: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" role="img" aria-hidden="true" {...props}>
      <title>WHOOP</title>
      <path
        d="M5 8.5h3.2l1.2 7 2.4-11 2.4 11 1.2-7H19"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PolarGlyph(props: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" role="img" aria-hidden="true" {...props}>
      <title>Polar</title>
      <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 6.5v3.2M12 14.3v3.2M6.5 12h3.2M14.3 12h3.2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="2" fill="currentColor" />
    </svg>
  );
}

function OuraGlyph(props: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" role="img" aria-hidden="true" {...props}>
      <title>Oura Ring</title>
      <circle cx="12" cy="12" r="7.5" stroke="currentColor" strokeWidth="2.2" />
      <path d="M9 12h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function UltrahumanGlyph(props: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" role="img" aria-hidden="true" {...props}>
      <title>Ultrahuman</title>
      <path
        d="M8 6.5v6.2a4 4 0 0 0 8 0V6.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M8 17.5c1 .9 2.3 1.4 4 1.4s3-.5 4-1.4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function FlowGlyph(props: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" role="img" aria-hidden="true" {...props}>
      <title>Flow</title>
      <path
        d="M12 4.5c3.2 3.7 5 6.5 5 9.1a5 5 0 0 1-10 0c0-2.6 1.8-5.4 5-9.1Z"
        fill="currentColor"
        opacity="0.2"
      />
      <path
        d="M12 4.5c3.2 3.7 5 6.5 5 9.1a5 5 0 0 1-10 0c0-2.6 1.8-5.4 5-9.1Z"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M9.7 13.2c.9.8 1.7 1.2 2.6 1.2s1.6-.4 2-1.2"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function WithingsGlyph(props: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" role="img" aria-hidden="true" {...props}>
      <title>Withings</title>
      <path
        d="M4 7 8 17l4-8 4 8 4-10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function OutlookCalendarGlyph(props: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" role="img" aria-hidden="true" {...props}>
      <title>Outlook Calendar</title>
      <rect x="3.5" y="5" width="17" height="15" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <rect x="4.5" y="10" width="7.5" height="4.5" fill="currentColor" opacity="0.25" />
      <rect x="12" y="10" width="7.5" height="4.5" fill="currentColor" opacity="0.15" />
      <rect x="4.5" y="14.5" width="7.5" height="4.5" fill="currentColor" opacity="0.15" />
      <rect x="12" y="14.5" width="7.5" height="4.5" fill="currentColor" opacity="0.25" />
    </svg>
  );
}

/**
 * Health Connect's brand mark is not in simple-icons, so this is the fallback:
 * the system health store as a shield with a pulse through it.
 */
function HealthConnectGlyph(props: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" role="img" aria-hidden="true" {...props}>
      <path
        d="M12 3.2 19 5.6v5.9c0 4-2.8 7.6-7 9.3-4.2-1.7-7-5.3-7-9.3V5.6L12 3.2Z"
        fill="currentColor"
        opacity="0.16"
      />
      <path
        d="M12 3.2 19 5.6v5.9c0 4-2.8 7.6-7 9.3-4.2-1.7-7-5.3-7-9.3V5.6L12 3.2Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M7.8 12.1h2l1.2-2.4 1.6 4 1.1-1.6h1.7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Google Health's mark is a heart drawn from two strokes. Not in simple-icons,
 * and deliberately distinct from the Google Fit glyph next to it in the list:
 * they are different providers reading different things.
 */
function GoogleHealthGlyph(props: SvgProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" role="img" aria-hidden="true" {...props}>
      <path
        d="M12 20.4 4.9 13.3a4.6 4.6 0 0 1 0-6.5 4.6 4.6 0 0 1 6.5 0l.6.6.6-.6a4.6 4.6 0 0 1 6.5 0 4.6 4.6 0 0 1 0 6.5L12 20.4Z"
        fill="currentColor"
        opacity="0.16"
      />
      <path
        d="M12 20.4 4.9 13.3a4.6 4.6 0 0 1 0-6.5 4.6 4.6 0 0 1 6.5 0l.6.6.6-.6a4.6 4.6 0 0 1 6.5 0 4.6 4.6 0 0 1 0 6.5L12 20.4Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type IconRenderer = ComponentType<SvgProps>;

const ICON: Record<ConnectionProvider, IconRenderer> = {
  terra: TerraGlyph,
  "apple-health": SiApple as IconRenderer,
  "health-connect": HealthConnectGlyph,
  google: SiGooglefit as IconRenderer,
  "google-health": GoogleHealthGlyph,
  fitbit: SiFitbit as IconRenderer,
  oura: OuraGlyph,
  garmin: SiGarmin as IconRenderer,
  whoop: WhoopGlyph,
  polar: PolarGlyph,
  peloton: SiPeloton as IconRenderer,
  ultrahuman: UltrahumanGlyph,
  strava: SiStrava as IconRenderer,
  myfitnesspal: MyFitnessPalGlyph,
  flow: FlowGlyph,
  withings: WithingsGlyph,
  dexcom: DexcomGlyph,
  "calendar-google": SiGooglecalendar as IconRenderer,
  "calendar-microsoft": OutlookCalendarGlyph,
  "ehr-epic": EpicMyChartGlyph,
  "ehr-cerner": OracleCernerGlyph,
};

export function ConnectionLogo({ provider, size = 44, className, variant }: ConnectionLogoProps) {
  const tint = TINT[provider];
  const Icon = ICON[provider];
  if (!tint || !Icon) return null;
  // Glyph variant: the bare brand mark, for callers that supply their own
  // chrome (the feature-card white puck). On white the mark wears its true
  // brand ink, so tile tints that exist for contrast swap to the real color.
  if (variant === "glyph") {
    const fg = provider === "apple-health" ? "text-gray-900" : tint.fg;
    return (
      <span
        className={cn("inline-flex shrink-0 items-center justify-center", className)}
        role="img"
        aria-label={CONNECTION_PROVIDER_LABELS[provider]}
      >
        {/* Fills the caller-sized wrapper so the mark can scale with CSS. */}
        <Icon className={cn(fg, "h-full w-full")} aria-hidden="true" />
      </span>
    );
  }
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-xl ring-1 ring-inset ring-black/[0.04]",
        tint.bg,
        className,
      )}
      style={{ width: size, height: size }}
      role="img"
      aria-label={CONNECTION_PROVIDER_LABELS[provider]}
    >
      <Icon
        className={cn(tint.fg)}
        width={Math.round(size * 0.55)}
        height={Math.round(size * 0.55)}
        aria-hidden="true"
      />
    </span>
  );
}
